import { cityFullLabel, ofCity, type City } from '../domain/index.ts';

/**
 * Registre des pages publiques et de leurs métadonnées (ADR-0006).
 * Utilisé par le Worker (HTML initial, sitemap) et par le front (<title>/<meta> React 19).
 * Le contexte géographique complet est TOUJOURS explicite : « Ermont » seul ne suffit pas.
 */
export interface PageMeta {
  readonly path: string;
  readonly title: string;
  readonly description: string;
  /** Titre visible et phrase d'introduction, servis aussi dans le HTML sans JavaScript. */
  readonly heading: string;
  readonly intro: string;
  readonly indexable: boolean;
  readonly inSitemap: boolean;
  /** Le contenu change tous les jours : le sitemap l'annonce avec la date du jour. */
  readonly updatedDaily: boolean;
}

export const SITE_NAME = 'Ermont Live';
export const SITE_TAGLINE = 'Tout ce qui se passe à Ermont, maintenant.';
export const INDEPENDENCE_NOTICE =
  "Ermont Live est un projet indépendant et n'est ni édité ni affilié à la Ville d'Ermont.";
export const COMMUNITY_NOTICE =
  'Carte communautaire indépendante. Un signalement publié ici ne remplace pas un signalement auprès des services compétents.';

type PageKey =
  | 'home'
  | 'transports'
  | 'map'
  | 'agenda'
  | 'roadworks'
  | 'places'
  | 'place'
  | 'sky'
  | 'water'
  | 'air'
  | 'search'
  | 'about'
  | 'legal'
  | 'privacy'
  | 'terms';

function pages(city: City): Record<PageKey, PageMeta> {
  const where = cityFullLabel(city); // « Ermont (Val-d'Oise, 95), Île-de-France, France »
  return {
    home: {
      path: '/',
      title: `${city.name} (${city.department.name}) maintenant : météo, transports, agenda | ${SITE_NAME}`,
      description: `Météo, pluie, qualité de l'air, prochains trains et vie locale à ${where}. Service indépendant, sources publiques citées.`,
      heading: SITE_NAME,
      intro: `${SITE_TAGLINE} Météo, qualité de l'air, transports et vie locale à ${where}.`,
      indexable: true,
      inSitemap: true,
      updatedDaily: true,
    },
    transports: {
      path: '/transports',
      title: `Prochains trains à ${city.name} (${city.department.code}) : gares, RER C, Transilien H et J | ${SITE_NAME}`,
      description: `Prochains départs et perturbations aux gares ${ofCity(city)} (Ermont - Eaubonne, Cernay, Ermont Halte, Gros Noyer Saint-Prix), ${city.department.name}, ${city.region}.`,
      heading: `Transports à ${city.name}`,
      intro: `Prochains départs et état des lignes aux gares de ${where}.`,
      indexable: true,
      inSitemap: true,
      updatedDaily: true,
    },
    map: {
      path: '/carte',
      title: `Carte ${ofCity(city)} (${city.department.name}) : gares, services, événements | ${SITE_NAME}`,
      description: `Carte interactive ${ofCity(city)}, ${city.department.name} (${city.department.code}) : gares, services publics, événements et informations locales.`,
      heading: `Carte ${ofCity(city)}`,
      intro: `Gares, services, événements et informations locales sur la carte de ${where}.`,
      indexable: true,
      inSitemap: true,
      updatedDaily: true,
    },
    agenda: {
      path: '/agenda',
      title: `Agenda ${ofCity(city)} (${city.department.code}) : événements à venir | ${SITE_NAME}`,
      description: `Événements à venir à ${where}, avec un lien vers chaque source officielle.`,
      heading: `Agenda ${ofCity(city)}`,
      intro: `Événements à venir à ${where}.`,
      indexable: true,
      inSitemap: true,
      updatedDaily: true,
    },
    roadworks: {
      path: '/travaux',
      title: `Travaux à ${city.name} (${city.department.code}) | ${SITE_NAME}`,
      description: `Travaux et chantiers à ${where}.`,
      heading: `Travaux à ${city.name}`,
      intro: `Travaux et chantiers en cours à ${where}.`,
      // Pas de source structurée en V0.1 (docs/07) : on n'indexe pas une page vide.
      indexable: false,
      inSitemap: false,
      updatedDaily: false,
    },
    places: {
      path: '/lieux',
      title: `Lieux et services publics à ${city.name} (${city.department.code}) | ${SITE_NAME}`,
      description: `Services publics, équipements et lieux utiles à ${where}.`,
      heading: `Lieux à ${city.name}`,
      intro: `Services publics, équipements et lieux utiles à ${where}.`,
      indexable: true,
      inSitemap: true,
      updatedDaily: false,
    },
    place: {
      path: '/lieux/:slug',
      title: `Lieu à ${city.name} (${city.department.code}) | ${SITE_NAME}`,
      description: `Informations pratiques sur un lieu à ${where}.`,
      heading: `Lieu à ${city.name}`,
      intro: `Informations pratiques sur un lieu à ${where}.`,
      indexable: true,
      inSitemap: false,
      updatedDaily: false,
    },
    sky: {
      path: '/ciel',
      title: `Le ciel ${ofCity(city)} : coucher de soleil, heure dorée, linge, parapluie | ${SITE_NAME}`,
      description: `Potentiel du coucher de soleil, heure dorée et indices du quotidien à ${where}, estimés à partir des prévisions Météo-France.`,
      heading: `Le ciel ${ofCity(city)}`,
      intro: `Coucher de soleil, heure dorée et indices du quotidien à ${where} : des estimations Ermont Live, pas des prévisions officielles.`,
      indexable: true,
      inSitemap: true,
      updatedDaily: true,
    },
    water: {
      path: '/eau',
      title: `Qualité de l’eau du robinet à ${city.name} (${city.department.code}) : dernière analyse | ${SITE_NAME}`,
      description: `Résultat du contrôle sanitaire de l’eau du robinet à ${where} : conformité, nitrates, dureté, chlore, avec la date du prélèvement.`,
      heading: `Qualité de l’eau du robinet à ${city.name}`,
      intro: `Le résultat du dernier contrôle sanitaire publié pour ${where}, et ce qu’il veut dire.`,
      indexable: true,
      inSitemap: true,
      // Le résultat ne change qu'à chaque prélèvement, espacé de plusieurs semaines.
      updatedDaily: false,
    },
    air: {
      path: '/air',
      title: `Qualité de l’air à ${city.name} (${city.department.code}) : indice ATMO du jour | ${SITE_NAME}`,
      description: `Indice ATMO du jour à ${where}, polluant responsable et concentrations mesurées, avec l’échelle officielle en six niveaux.`,
      heading: `Qualité de l’air à ${city.name}`,
      intro: `L’indice ATMO du jour à ${where}, et comment il est construit.`,
      indexable: true,
      inSitemap: true,
      updatedDaily: true,
    },
    search: {
      path: '/recherche',
      title: `Rechercher à ${city.name} : lieux, commerces, événements | ${SITE_NAME}`,
      description: `Rechercher un lieu, un commerce ou un événement à ${where}.`,
      heading: 'Rechercher',
      intro: `Lieux, commerces et événements à ${where}.`,
      // Page de résultats : jamais indexée.
      indexable: false,
      inSitemap: false,
      updatedDaily: false,
    },
    legal: {
      path: '/mentions-legales',
      title: `Mentions légales | ${SITE_NAME}`,
      description: `Éditeur, hébergeur, licences des données et indépendance de ${SITE_NAME}, service d’information locale à ${where}.`,
      heading: 'Mentions légales',
      intro: `Éditeur, hébergeur et licences des données affichées sur ${SITE_NAME}.`,
      indexable: true,
      inSitemap: true,
      updatedDaily: false,
    },
    privacy: {
      path: '/confidentialite',
      title: `Confidentialité | ${SITE_NAME}`,
      description: `Aucun compte, aucun cookie, aucun traceur publicitaire : ce que ${SITE_NAME} traite, et ce qu’il ne traite pas.`,
      heading: 'Confidentialité',
      intro: 'Aucun compte, aucun cookie, aucun traceur publicitaire. Voici ce qui est traité, et par qui.',
      indexable: true,
      inSitemap: true,
      updatedDaily: false,
    },
    terms: {
      path: '/cgu',
      title: `Conditions d’utilisation | ${SITE_NAME}`,
      description: `Conditions d’utilisation de ${SITE_NAME} : informations indicatives, sources officielles à consulter avant toute décision.`,
      heading: 'Conditions d’utilisation',
      intro:
        'Des informations indicatives, issues de sources publiques : à vérifier auprès des sources officielles avant toute décision.',
      indexable: true,
      inSitemap: false,
      updatedDaily: false,
    },
    about: {
      path: '/a-propos',
      title: `À propos, sources et confidentialité | ${SITE_NAME}`,
      description: `${INDEPENDENCE_NOTICE} Sources de données, licences, confidentialité.`,
      heading: 'À propos',
      intro: INDEPENDENCE_NOTICE,
      indexable: true,
      inSitemap: true,
      updatedDaily: false,
    },
  };
}

/** Métadonnées d'un chemin, ou null s'il ne correspond à aucune page publique. */
export function pageMetaForPath(pathname: string, city: City): PageMeta | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const all = pages(city);
  if (/^\/lieux\/[a-z0-9-]{1,80}$/.test(path)) return { ...all.place, path };
  return Object.values(all).find((p) => p.path === path) ?? null;
}

export function sitemapPages(city: City): PageMeta[] {
  return Object.values(pages(city)).filter((p) => p.inSitemap);
}
